import styled, { css } from 'styled-components'

export interface PagerProps {
  page: number
  pageCount: number
  onPage: (page: number) => void
}

type PageItem = number | 'gap'

/** Compact page list: first, a window around the current page, last — with gaps. */
const buildPages = (page: number, pageCount: number): PageItem[] => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1)
  }
  const items: PageItem[] = [1]
  const start = Math.max(2, page - 1)
  const end = Math.min(pageCount - 1, page + 1)
  if (start > 2) items.push('gap')
  for (let p = start; p <= end; p++) items.push(p)
  if (end < pageCount - 1) items.push('gap')
  items.push(pageCount)
  return items
}

const ChevronLeft = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
)
const ChevronRight = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

export const Pager = ({ page, pageCount, onPage }: PagerProps) => (
  <Root>
    {/* RTL: "previous" sits visually on the start (right) side via the chevron pointing right. */}
    <Btn
      type="button"
      disabled={page <= 1}
      onClick={() => onPage(page - 1)}
      aria-label="הקודם"
    >
      {ChevronRight}
    </Btn>
    <PageList>
      {buildPages(page, pageCount).map((item, i) =>
        item === 'gap' ? (
          <Gap key={`gap-${i}`}>…</Gap>
        ) : (
          <Btn
            key={item}
            type="button"
            $active={item === page}
            onClick={() => onPage(item)}
            aria-current={item === page ? 'page' : undefined}
          >
            {item}
          </Btn>
        ),
      )}
    </PageList>
    {/* Mobile: just the current page, no number list. */}
    <MobilePage>
      {page} / {pageCount}
    </MobilePage>
    <Btn
      type="button"
      disabled={page >= pageCount}
      onClick={() => onPage(page + 1)}
      aria-label="הבא"
    >
      {ChevronLeft}
    </Btn>
  </Root>
)

export default Pager

const Root = styled.div`
  display: inline-flex;
  gap: 4px;
  align-items: center;
`

const PageList = styled.div`
  display: inline-flex;
  gap: 4px;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`

const MobilePage = styled.span`
  display: none;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
  padding: 0 6px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: inline-flex;
    align-items: center;
  }
`

const Btn = styled.button<{ $active?: boolean }>`
  min-width: 30px;
  height: 30px;
  padding: 0 6px;
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radii.sm};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.sm};
  color: ${({ theme }) => theme.colors.ink2};
  cursor: pointer;
  display: grid;
  place-items: center;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
    background: ${({ theme }) => theme.colors.bgLayout};
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.ink4};
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    width: 14px;
    height: 14px;
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.ink};
      border-color: ${theme.colors.ink};
      color: #fff;
      &:hover:not(:disabled) {
        background: ${theme.colors.ink};
        color: #fff;
      }
    `}
`

const Gap = styled.span`
  min-width: 24px;
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.sm};
  color: ${({ theme }) => theme.colors.ink4};
`
